import { Box, Card, Stack, TypographyProps } from '@mui/material';
import ArticleCoverImage from '../Article/ArticleCoverImage';
import ArticleMeta from '../../types/ArticleMeta';
import HighlightedArticleCard from './HighlightedArticleCard';
import OverlineSection from './OverlineSection';
import MainContentArea from './MainContentArea';
import { ArticleCardVariant, CardGTMData } from './CardUtils';
import ArticleCardFooter from './ArticleCardFooter';
import {
  articleCardClickTarget,
  sendArticleCardClickToGTM,
} from '../../utilities/gtm';
import HighlightedMinimizedArticleCard from './HighlightedMinimizedArticleCard';

export interface ArticleCardProps {
  readonly meta: ArticleMeta;
  readonly variant?: ArticleCardVariant;
  readonly gtmData?: CardGTMData;
  readonly heading?: TypographyProps['component'];
  readonly hidePrimaryCategory?: boolean;
  readonly hideCategories?: boolean;
  readonly showOnlyCustomCoverImage?: boolean;
  readonly hideCoverImage?: boolean;
  readonly imageAspectRatio?: number;
}

export default function ArticleCard({
  meta,
  gtmData = {},
  variant = 'default',
  heading = 'h2',
  hidePrimaryCategory = false,
  hideCategories = false,
  showOnlyCustomCoverImage = false,
  hideCoverImage = false,
  imageAspectRatio,
}: ArticleCardProps) {
  const commonProps = {
    meta,
    gtmData,
    imageAspectRatio,
    heading,
  };

  if (variant === 'minimizedHighlight') {
    return <HighlightedMinimizedArticleCard {...commonProps} />;
  }

  if (variant === 'highlight') {
    return (
      <HighlightedArticleCard
        {...commonProps}
        hidePrimaryCategory={hidePrimaryCategory}
        hideCategories={hideCategories}
        showOnlyCustomCoverImage={showOnlyCustomCoverImage}
        hideCoverImage={hideCoverImage}
      />
    );
  }

  const showCoverImage = !(
    hideCoverImage ||
    (showOnlyCustomCoverImage && !meta.hasCoverImage())
  );

  return (
    <Card
      component='article'
      sx={{
        paddingX: 2,
        paddingTop: 2,
        // The touch target in the bottom takes space from the bottom padding
        paddingBottom: variant === 'mobile' ? 0.25 : 2,
        height: '100%',
      }}
      variant='outlined'
    >
      <Stack direction='column'>
        <Stack
          direction='row'
          sx={{
            justifyContent: 'space-between',
            marginBottom: 1,
          }}
        >
          <OverlineSection type={meta.type} openContent={meta.isPublic} />
        </Stack>

        <Stack
          direction='row'
          useFlexGap
          sx={{
            width: '100%',
            columnGap: 3,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <MainContentArea
              meta={meta}
              heading={heading}
              variant={variant}
              showOnlyCustomCoverImage={showOnlyCustomCoverImage}
              hideCoverImage={hideCoverImage}
              onTitleClick={() => {
                sendArticleCardClickToGTM(
                  meta,
                  articleCardClickTarget.ARTICLE,
                  gtmData.location,
                  gtmData.index,
                );
              }}
            />
            <ArticleCardFooter
              variant={variant}
              meta={meta}
              hidePrimaryCategory={hidePrimaryCategory}
              hideCategories={hideCategories}
              onAuthorClick={() => {
                sendArticleCardClickToGTM(
                  meta,
                  articleCardClickTarget.AUTHOR,
                  gtmData.location,
                  gtmData.index,
                );
              }}
              onCategoryClick={() => {
                sendArticleCardClickToGTM(
                  meta,
                  articleCardClickTarget.CATEGORY,
                  gtmData.location,
                  gtmData.index,
                );
              }}
            />
          </Box>
          {variant === 'default' && showCoverImage && (
            <Box
              sx={{
                marginBottom: 4,
                flexShrink: 0,
                width: '148px',
              }}
            >
              <ArticleCoverImage
                src={meta.coverImage}
                category={
                  /* Only define the category if default cover image is shown. If 
                    only custom cover image is shown, the category shoulod not be
                    defined so that the default category image won't be shown as a
                    fallback option. */
                  showOnlyCustomCoverImage ? undefined : meta.categories.primary
                }
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
